import React, { useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import constantContactAPI from '../utils/constantContact';
import { getJobValue } from '../data/industryTemplates';

function NotificationAnimation() {
  const [showSpam, setShowSpam] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setShowSpam(s => !s), 2000);
    return () => clearInterval(interval);
  }, []);

  const missedCall = {
    number: '(555) 123-4567',
    label: 'Missed Call',
    time: '9:41 AM',
    avatarColor: 'bg-gray-300',
    icon: (
      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
      </svg>
    )
  };
  const spamCall = {
    number: '(888) 555-0000',
    label: 'Spam Risk',
    time: '9:42 AM',
    avatarColor: 'bg-gray-300',
    icon: (
      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
      </svg>
    )
  };
  const notif = showSpam ? spamCall : missedCall;

  return (
    <div className="w-full max-w-sm mx-auto mb-6 bg-gray-900 rounded-2xl p-4 text-white relative overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-gray-400">9:41</div>
        <div className="flex items-center space-x-1">
          <div className="w-1 h-1 bg-white rounded-full"></div>
          <div className="w-1 h-1 bg-white rounded-full"></div>
          <div className="w-1 h-1 bg-white rounded-full"></div>
          <div className="text-xs text-gray-400">100%</div>
        </div>
      </div>
      <div className="bg-gray-800 rounded-xl p-3 transition-all duration-500">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 ${notif.avatarColor} rounded-full flex items-center justify-center`}>
            {notif.icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">{notif.label}</span>
              <span className="text-xs text-gray-400">{notif.time}</span>
            </div>
            <div className="text-sm text-gray-300">{notif.number}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LossBlock({ amount, show }) {
  if (!show || !amount) return null;
  
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 animate-fade-in">
      <div className="text-center">
        <p className="text-red-800 font-semibold text-lg">Potential Annual Loss:</p>
        <p className="text-red-900 font-bold text-3xl">
          ${amount.toLocaleString()}
        </p>
        <p className="text-red-700 text-sm mt-1">Based on your answers</p>
      </div>
    </div>
  );
}

function QuizFunnel({ agentConfig }) {
  const [page, setPage] = useState('landing');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showLoss, setShowLoss] = useState(false);
  const [lossAmount, setLossAmount] = useState(0);
  const [displayLoss, setDisplayLoss] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [lossCalculated, setLossCalculated] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [formError, setFormError] = useState('');

  const { questions, messaging, branding } = agentConfig;

  // Load Calendly script when thankyou page is displayed
  useEffect(() => {
    if (page === 'thankyou' && agentConfig.industry === 'Therapist/Mental Health') {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);

      return () => {
        // Cleanup script when component unmounts or page changes
        const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
        if (existingScript) {
          document.body.removeChild(existingScript);
        }
      };
    }
  }, [page, agentConfig.industry]);

  // Animation for number counter
  useEffect(() => {
    if (showLoss) {
      const end = lossAmount;
      if (end === 0) return;
      const duration = 1200;
      const stepTime = Math.max(Math.floor(duration / end), 20);
      let current = 0;
      const timer = setInterval(() => {
        current += Math.ceil(end / (duration / stepTime));
        if (current >= end) {
          setDisplayLoss(end);
          clearInterval(timer);
        } else {
          setDisplayLoss(current);
        }
      }, stepTime);
      return () => clearInterval(timer);
    }
  }, [showLoss, lossAmount]);

  function handleOptionClick(option) {
    setAnswers([...answers, option]);
    const isTherapist = agentConfig.industry === 'Therapist/Mental Health';

    if (step === 0) {
      setTimeout(() => setShowLoss(false), 500);
    }
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      if (isTherapist) {
        setPage('form');
      } else {
        setTimeout(() => setShowConfetti(true), 600);
        setPage('result');
      }
    }
  }

  function handleRestart() {
    setStep(0);
    setAnswers([]);
    setShowLoss(false);
    setLossAmount(0);
    setDisplayLoss(0);
    setShowConfetti(false);
    setPhone('');
    setPhoneError('');
    setLossCalculated(false);
    setForm({ name: '', email: '' });
    setFormError('');
    setPage('landing');
  }

  function handleFormChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleFormSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      setFormError('Please enter a valid name and email.');
      return;
    }

    setFormError('');

    try {
      // Save to Firebase (existing functionality)
      const quizData = {
        name: form.name,
        email: form.email,
        answers: answers,
        agentId: agentConfig.id,
        agentName: agentConfig.name,
        industry: agentConfig.industry,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct',
        // Individual answer breakdown for therapist quiz
        mentalHealthReason: answers[0] || '',
        currentMood: answers[1] || '',
        dailyLifeImpact: answers[2] || '',
        therapyGoals: answers[3] || '',
        previousTherapyExperience: answers[4] || ''
      };

      await addDoc(collection(db, 'quiz_responses'), quizData);
      console.log('Quiz response saved to Firebase successfully');

      // Add to Constant Contact if authenticated (for Erika's therapist quiz)
      if (agentConfig.industry === 'Therapist/Mental Health' && constantContactAPI.isAuthenticated()) {
        try {
          await constantContactAPI.addContact(form.email, form.name);
          console.log('Contact added to Constant Contact successfully');
        } catch (ccError) {
          console.error('Failed to add contact to Constant Contact:', ccError);
          // Don't block the user flow if Constant Contact fails
        }
      }

      // Fire Google Ads conversion tracking for therapy quiz
      if (agentConfig.industry === 'Therapist/Mental Health' && typeof window.gtag_report_conversion === 'function') {
        try {
          window.gtag_report_conversion();
          console.log('Google Ads conversion tracked successfully');
        } catch (conversionError) {
          console.error('Failed to track Google Ads conversion:', conversionError);
          // Don't block the user flow if conversion tracking fails
        }
      }

      // Conversion tracking removed

      setPage('thankyou');
    } catch (error) {
      console.error('Error saving quiz response:', error);
      setFormError('There was an error submitting your information. Please try again.');
    }
  }

  // Show loss after job value question
  useEffect(() => {
    if (step >= 2 && answers.length >= 2 && !lossCalculated) {
      const missed = answers[0];
      if (missed !== "None") {
        let jobsMissed = 0;
        if (missed === "1-2" || missed === "1-3") jobsMissed = 2;
        else if (missed === "3-5" || missed === "4-8") jobsMissed = 4;
        else if (missed === "More than 5" || missed === "More than 8") jobsMissed = 6;
        else jobsMissed = 1;
        const jobValue = getJobValue(answers[1], agentConfig.industry);
        const annualLoss = jobValue * jobsMissed * 52;
        setLossAmount(annualLoss);
        setShowLoss(true);
        setLossCalculated(true);
      }
    }
  }, [step, answers, lossCalculated, agentConfig.industry]);

  async function handlePhoneSubmit() {
    if (!phone.trim()) {
      setPhoneError('Phone number is required');
      return;
    }

    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setPhoneError('Please enter a valid phone number');
      return;
    }

    try {
      const quizData = {
        phone: phone,
        answers: answers,
        lossAmount: lossAmount,
        agentId: agentConfig.id,
        agentName: agentConfig.name,
        industry: agentConfig.industry,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct',
        // Individual answer breakdown
        missedCallsPerMonth: answers[0] || '',
        jobValue: answers[1] || '',
        biggestFrustration: answers[2] || '',
        currentHandling: answers[3] || '',
        timeSaved: answers[4] || '',
        schedulingSoftware: answers[5] || '',
        earlyAdopterInterest: answers[6] || ''
      };

      await addDoc(collection(db, 'quiz_responses'), quizData);
      console.log('Quiz response saved successfully');

      // Conversion tracking removed

      setPage('confirm');
    } catch (error) {
      console.error('Error saving quiz response:', error);
      setPhoneError('There was an error submitting your information. Please try again.');
    }
  }

  const gradientClass = `bg-gradient-to-br from-${branding.gradientFrom} to-${branding.gradientTo}`;
  const buttonClass = `bg-${branding.buttonColor} hover:bg-${branding.buttonHover}`;

  // Landing Page
  if (page === 'landing') {
    const isTherapist = agentConfig.industry === 'Therapist/Mental Health';

    if (isTherapist) {
      // Therapy-specific landing page
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#eafaf2] to-white flex flex-col items-center px-2 py-6">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 flex flex-col items-center animate-fade-in border border-[#eafaf2]">
            <div className="w-32 h-32 rounded-full overflow-hidden mb-4 border-4 border-green-600 bg-white flex items-center justify-center shadow-md">
              <img src="https://cdn.abacus.ai/images/74b7b16b-c4f7-4e7a-8b94-2fb3b6076959.png" alt="Erika Paz" className="w-full h-full object-cover" />
            </div>
            <h1 className="text-2xl font-bold text-green-600 mb-1 text-center">Erika Paz</h1>
            <div className="text-green-600 text-lg font-semibold text-center mb-2">Find Relief from Anxiety & Depression with Virtual Sessions in Texas</div>
            <div className="text-xs text-gray-700 mb-1 text-center font-semibold">Accepts most insurance in TX</div>
            <div className="text-xs text-gray-700 mb-2 text-center font-semibold">Accepts Medicare and Medicaid</div>
            <div className="text-xs text-green-600 mb-4 text-center font-semibold">Specialties: Anxiety, Bipolar Disorder, Depression, Relationship Issues, Substance Use / Addiction</div>
            <div className="w-full border-t border-[#eafaf2] my-2"></div>
            <div className="w-full mb-4">
              <div className="text-base text-green-600 font-semibold mb-2 text-center">Not sure if therapy is right for you?</div>
              <ul className="text-sm text-gray-700 mb-4 space-y-2">
                <li>
                  <svg className="inline-block mr-2 text-green-600" width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="12" fill="#eafaf2"/>
                    <path d="M7 13l3 3 7-7" stroke="#11aa64" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Find out if therapy could help you feel better
                </li>
                <li>
                  <svg className="inline-block mr-2 text-green-600" width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="12" fill="#eafaf2"/>
                    <path d="M7 13l3 3 7-7" stroke="#11aa64" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Get a personalized recommendation for your needs
                </li>
                <li>
                  <svg className="inline-block mr-2 text-green-600" width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="12" fill="#eafaf2"/>
                    <path d="M7 13l3 3 7-7" stroke="#11aa64" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  See if you're a good fit for virtual sessions with Erika
                </li>
                <li>
                  <svg className="inline-block mr-2 text-green-600" width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="12" fill="#eafaf2"/>
                    <path d="M7 13l3 3 7-7" stroke="#11aa64" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Takes less than 1 minute, no commitment required
                </li>
              </ul>
            </div>
            <button
              className="w-full py-3 px-4 mb-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg text-center transition-all duration-200 text-lg transform hover:scale-105 active:scale-95 focus:outline-none animate-pulse-once"
              onClick={() => setPage('quiz')}
            >
              Take the Quiz
            </button>
            <a
              href="https://care.headway.co/providers/erika-paz-2?utm_source=pem&utm_medium=direct_link&utm_campaign=130375&fbclid=IwY2xjawLRAyBleHRuA2FlbQIxMABicmlkETF6MTZGd29OdkRUWmtmZVk2AR6IzKa0tU0jF0qHQbgWs9tgpd3OfYQbsaUr8uXKb7lUvprnvcIRyu3RG57JKg_aem_0BcFXrfBMwGmZrwxUUJGag"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg text-center transition-all duration-200 border border-green-600 mt-2 transform hover:scale-105 active:scale-95 focus:outline-none"
            >
              Book Now
            </a>
            <div className="text-xs text-gray-400 mt-4 text-center">This quiz is for informational purposes only and is not HIPAA-compliant. Please do not submit sensitive health information.</div>
          </div>
          <style>{`
            .animate-fade-in { animation: fadeIn 0.5s; }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            .animate-pulse-once { animation: pulseOnce 0.7s 1; }
            @keyframes pulseOnce { 0% { transform: scale(1); } 50% { transform: scale(1.07); } 100% { transform: scale(1); } }
          `}</style>
        </div>
      );
    }

    // Original landing page for other industries
    return (
      <div className={`min-h-screen ${gradientClass} flex flex-col items-center px-2 py-6`}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 flex flex-col items-center animate-fade-in border border-[#eafaf2]">
          <NotificationAnimation />
          <h1 className={`text-2xl font-bold text-${branding.buttonColor} mb-1 text-center`}>{agentConfig.name}</h1>
          <div className={`text-${branding.buttonColor} text-lg font-semibold text-center mb-2`}>{messaging.subtitle}</div>
          <div className="text-xs text-gray-700 mb-4 text-center font-semibold">{agentConfig.industry}</div>
          <div className="w-full border-t border-[#eafaf2] my-2"></div>
          <div className="w-full mb-4">
            <div className={`text-base text-${branding.buttonColor} font-semibold mb-2 text-center`}>{messaging.title}</div>
            <ul className="text-sm text-gray-700 mb-4 space-y-2">
              {messaging.benefits.map((benefit, idx) => (
                <li key={idx}>
                  <svg className="inline-block mr-2 text-green-600" width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="12" fill="#eafaf2"/>
                    <path d="M7 13l3 3 7-7" stroke="#11aa64" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
          <LossBlock amount={displayLoss} show={showLoss} />
          <button
            className={`w-full py-3 px-4 mb-3 ${buttonClass} text-white rounded-xl font-bold shadow-lg text-center transition-all duration-200 text-lg transform hover:scale-105 active:scale-95 focus:outline-none animate-pulse-once`}
            onClick={() => setPage('quiz')}
          >
            Take the Quiz
          </button>
          <div className="text-xs text-gray-400 mt-4 text-center">This quiz is for informational purposes only and is not HIPAA-compliant. Please do not submit sensitive health information.</div>
        </div>
        <style>{`
          .animate-fade-in { animation: fadeIn 0.5s; }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          .animate-pulse-once { animation: pulseOnce 0.7s 1; }
          @keyframes pulseOnce { 0% { transform: scale(1); } 50% { transform: scale(1.07); } 100% { transform: scale(1); } }
        `}</style>
      </div>
    );
  }

  // Quiz Page
  if (page === 'quiz') {
    const isTherapist = agentConfig.industry === 'Therapist/Mental Health';

    if (isTherapist) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#eafaf2] to-white flex flex-col items-center px-2 py-6">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 animate-fade-in border border-[#eafaf2]">
            <div className="mb-6">
              <div className="h-2 bg-[#eafaf2] rounded-full">
                <div className="h-2 bg-green-600 rounded-full transition-all duration-300" style={{ width: `${((step + 1) / (questions.length + 1)) * 100}%` }}></div>
              </div>
              <div className="text-xs text-gray-500 mt-1 text-right">Step {step + 1} of {questions.length + 1}</div>
            </div>
            <h2 className="text-lg font-semibold mb-4 text-green-600">{questions[step].question}</h2>
            <div className="space-y-3">
              {questions[step].options.map((option, idx) => (
                <button key={idx} className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium text-left shadow-sm transition-all duration-200 transform hover:scale-105 active:scale-95 focus:outline-none animate-pulse-once" onClick={() => handleOptionClick(option)}>{option}</button>
              ))}
            </div>
          </div>
          <style>{`
            .animate-fade-in { animation: fadeIn 0.5s; }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            .animate-pulse-once { animation: pulseOnce 0.7s 1; }
            @keyframes pulseOnce { 0% { transform: scale(1); } 50% { transform: scale(1.07); } 100% { transform: scale(1); } }
          `}</style>
        </div>
      );
    }

    // Original quiz page for other industries
    return (
      <div className={`min-h-screen ${gradientClass} flex flex-col items-center px-2 py-6`}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 animate-fade-in border border-[#eafaf2]">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="h-2 bg-gray-100 rounded-full">
              <div
                className={`h-2 bg-${branding.buttonColor} rounded-full transition-all duration-300`}
                style={{ width: `${((step + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 mt-1 text-right">
              Step {step + 1} of {questions.length}
            </div>
          </div>

          {/* Quiz Content */}
          <div className="transition-all duration-500 animate-fade-in">
            <h2 className={`text-lg font-semibold mb-4 text-${branding.buttonColor}`}>
              {questions[step].question}
            </h2>
            <LossBlock amount={displayLoss} show={showLoss} />
            <div className="space-y-3">
              {questions[step].options.map((option, index) => (
                <button
                  key={index}
                  className={`w-full py-3 px-4 ${buttonClass} text-white rounded-xl font-medium text-left shadow-sm transition-all duration-200 transform hover:scale-105 active:scale-95 focus:outline-none animate-pulse-once`}
                  onClick={() => handleOptionClick(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form Page (for therapy template)
  if (page === 'form') {
    const isTherapist = agentConfig.industry === 'Therapist/Mental Health';

    if (isTherapist) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#eafaf2] to-white flex flex-col items-center px-2 py-6">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 animate-fade-in border border-[#eafaf2]">
            <h2 className="text-lg font-semibold mb-4 text-green-600">What's your first name and best email so I can reach out with next steps?</h2>
            <form className="w-full" onSubmit={handleFormSubmit}>
              <input type="text" name="name" placeholder="First name" className="w-full py-3 px-4 mb-2 border border-green-600 rounded-xl text-green-600 focus:outline-none focus:ring-2 focus:ring-green-600 transition-all duration-200 text-lg" value={form.name} onChange={handleFormChange} autoFocus />
              <input type="email" name="email" placeholder="Email address" className="w-full py-3 px-4 mb-2 border border-green-600 rounded-xl text-green-600 focus:outline-none focus:ring-2 focus:ring-green-600 transition-all duration-200 text-lg" value={form.email} onChange={handleFormChange} />
              {formError && <div className="text-red-600 text-sm mb-2">{formError}</div>}
              <button type="submit" className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95 focus:outline-none animate-pulse-once">See My Results</button>
            </form>
          </div>
          <style>{`
            .animate-fade-in { animation: fadeIn 0.5s; }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            .animate-pulse-once { animation: pulseOnce 0.7s 1; }
            @keyframes pulseOnce { 0% { transform: scale(1); } 50% { transform: scale(1.07); } 100% { transform: scale(1); } }
          `}</style>
        </div>
      );
    }

    // Original form page for other industries
    return (
      <div className={`min-h-screen ${gradientClass} flex flex-col items-center px-2 py-6`}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 animate-fade-in border border-[#eafaf2]">
          <h2 className={`text-lg font-semibold mb-4 text-${branding.buttonColor}`}>
            What's your first name and best email so I can reach out with next steps?
          </h2>
          <form className="w-full" onSubmit={handleFormSubmit}>
            <input
              type="text"
              name="name"
              placeholder="First name"
              className={`w-full py-3 px-4 mb-2 border border-${branding.buttonColor} rounded-xl text-${branding.buttonColor} focus:outline-none focus:ring-2 focus:ring-${branding.buttonColor} transition-all duration-200 text-lg`}
              value={form.name}
              onChange={handleFormChange}
              autoFocus
            />
            <input
              type="email"
              name="email"
              placeholder="Email address"
              className={`w-full py-3 px-4 mb-2 border border-${branding.buttonColor} rounded-xl text-${branding.buttonColor} focus:outline-none focus:ring-2 focus:ring-${branding.buttonColor} transition-all duration-200 text-lg`}
              value={form.email}
              onChange={handleFormChange}
            />
            {formError && <div className="text-red-600 text-sm mb-2">{formError}</div>}
            <button
              type="submit"
              className={`w-full py-3 px-4 ${buttonClass} text-white rounded-xl font-bold shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95 focus:outline-none animate-pulse-once`}
            >
              See My Results
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Thank You Page (for therapy template)
  if (page === 'thankyou') {
    const isTherapist = agentConfig.industry === 'Therapist/Mental Health';

    if (isTherapist) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#eafaf2] to-white flex flex-col items-center px-2 py-6">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6 flex flex-col items-center animate-fade-in border border-[#eafaf2]">
            <div className="w-24 h-24 rounded-full overflow-hidden mb-3 border-4 border-green-600 bg-white flex items-center justify-center shadow-md">
              <img src="https://cdn.abacus.ai/images/74b7b16b-c4f7-4e7a-8b94-2fb3b6076959.png" alt="Erika Paz" className="w-full h-full object-cover" />
            </div>
            <h2 className="text-2xl font-bold text-green-600 mb-2 text-center">Online therapy is a perfect fit for you!</h2>
            <div className="text-gray-700 text-center mb-4 text-lg">Book a time now and submit your insurance information—all in one simple step. The process is quick and straightforward, so you can get started on the right track.</div>

            <div className="text-center mb-4">
              <span className="text-green-600 font-bold text-lg">- OR -</span>
            </div>

            {/* Calendly Discovery Call Widget */}
            <div className="w-full mb-6">
              <h3 className="text-xl font-semibold text-green-600 mb-2 text-center">Book Your Free 15-Minute Discovery Call</h3>
              <p className="text-gray-600 text-center mb-4 text-sm">This is a no-obligation call to discuss your therapy needs and see if we're a good fit.</p>
              <div className="w-full bg-white rounded-xl shadow-sm border border-green-200 overflow-hidden">
                <div
                  className="calendly-inline-widget"
                  data-url="https://calendly.com/erickaramos1205/30min?hide_gdpr_banner=1"
                  style={{ minWidth: '320px', height: '700px' }}
                ></div>
              </div>
            </div>

            <a
              href="https://care.headway.co/providers/erika-paz-2?utm_source=pem&utm_medium=direct_link&utm_campaign=130375&fbclid=IwY2xjawLRAyBleHRuA2FlbQIxMABicmlkETF6MTZGd29OdkRUWmtmZVk2AR6IzKa0tU0jF0qHQbgWs9tgpd3OfYQbsaUr8uXKb7lUvprnvcIRyu3RG57JKg_aem_0BcFXrfBMwGmZrwxUUJGag"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 px-4 mt-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg text-center transition-all duration-200 transform hover:scale-105 active:scale-95 focus:outline-none animate-pulse-once"
            >
              Book Full Session
            </a>
          </div>
          <style>{`
            .animate-fade-in { animation: fadeIn 0.5s; }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            .animate-pulse-once { animation: pulseOnce 0.7s 1; }
            @keyframes pulseOnce { 0% { transform: scale(1); } 50% { transform: scale(1.07); } 100% { transform: scale(1); } }

            /* Responsive Calendly widget */
            @media (max-width: 768px) {
              .calendly-inline-widget {
                height: 600px !important;
                min-height: 600px !important;
              }
            }
          `}</style>
        </div>
      );
    }

    // Original thank you page for other industries
    return (
      <div className={`min-h-screen ${gradientClass} flex flex-col items-center px-2 py-6`}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 flex flex-col items-center animate-fade-in border border-[#eafaf2]">
          {agentConfig.profileImage && (
            <div className={`w-24 h-24 rounded-full overflow-hidden mb-3 border-4 border-${branding.buttonColor} bg-white flex items-center justify-center shadow-md`}>
              <img src={agentConfig.profileImage} alt={agentConfig.name} className="w-full h-full object-cover" />
            </div>
          )}
          <h2 className={`text-2xl font-bold text-${branding.buttonColor} mb-2 text-center`}>
            {messaging.resultTitle}
          </h2>
          <div className="text-gray-700 text-center mb-4 text-lg">
            {messaging.resultSubtitle}
          </div>
          {messaging.bookingUrl && (
            <a
              href={messaging.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full py-3 px-4 mt-2 ${buttonClass} text-white rounded-xl font-bold shadow-lg text-center transition-all duration-200 transform hover:scale-105 active:scale-95 focus:outline-none animate-pulse-once`}
            >
              Book My Session
            </a>
          )}
        </div>
      </div>
    );
  }

  // Result Page
  if (page === 'result') {
    return (
      <div className={`min-h-screen ${gradientClass} flex flex-col items-center justify-center px-2 py-6`}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 text-center animate-fade-in">
          {showConfetti && <div className="confetti"></div>}
          <h2 className={`text-2xl font-bold text-${branding.buttonColor} mb-4`}>
            {messaging.resultTitle}
          </h2>
          <p className="text-gray-700 mb-4 text-center">{messaging.resultSubtitle}</p>
          <LossBlock amount={displayLoss} show={showLoss} />
          <button
            className={`w-full flex items-center justify-center gap-2 py-3 px-4 ${buttonClass} text-white rounded-xl font-semibold shadow-md transition-all duration-200 mb-2 animate-bounce-in`}
            onClick={() => setPage('phone')}
          >
            Get My Early Access & Direct Line
          </button>
          <button
            className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors duration-200"
            onClick={handleRestart}
          >
            Retake Quiz
          </button>
        </div>
      </div>
    );
  }

  // Phone Collection Page
  if (page === 'phone') {
    return (
      <div className={`min-h-screen ${gradientClass} flex flex-col items-center justify-center px-2 py-6`}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 animate-fade-in">
          <h2 className={`text-2xl font-bold text-${branding.buttonColor} mb-4 text-center`}>
            Get Your Direct Line to Early Access
          </h2>
          <p className="text-gray-700 mb-6 text-center">
            Enter your phone number to secure your spot in our exclusive early adopter program.
          </p>
          <div className="mb-4">
            <input
              type="tel"
              placeholder="Your phone number"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneError('');
              }}
              className={`w-full p-4 border-2 rounded-xl font-medium ${
                phoneError ? 'border-red-500' : 'border-gray-200 focus:border-blue-500'
              } focus:outline-none transition-colors duration-200`}
            />
            {phoneError && <p className="text-red-500 text-sm mt-2">{phoneError}</p>}
          </div>
          <button
            className={`w-full py-3 px-4 ${buttonClass} text-white rounded-xl font-semibold shadow-md transition-all duration-200 mb-2`}
            onClick={handlePhoneSubmit}
          >
            Secure My Early Access
          </button>
          <button
            className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors duration-200"
            onClick={() => setPage('result')}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  // Confirmation Page
  if (page === 'confirm') {
    return (
      <div className={`min-h-screen ${gradientClass} flex flex-col items-center justify-center px-2 py-6`}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 text-center animate-fade-in">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-green-600 mb-4">You're In!</h2>
          <p className="text-gray-700 mb-6">
            Thank you for joining our early adopter program. We'll be in touch soon with exclusive access and updates.
          </p>
          <button
            className={`w-full py-3 px-4 ${buttonClass} text-white rounded-xl font-semibold shadow-md transition-all duration-200`}
            onClick={handleRestart}
          >
            Take Quiz Again
          </button>
        </div>
      </div>
    );
  }
}

export default QuizFunnel;
